import {
    Dialog,
    DialogBackdrop,
    DialogPanel,
    DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import { useAsync } from "react-use";
import { HISTORIC_DATA_ENDPOINT } from "../../../api/endpoints";
import { fetcher } from "../../../api/utils";
import useTestId from "../../../hooks/useTestId";
import { TestSearchResult, TestSearchResultFromAPI } from "../../../types";
import { convertSearchResultsFromAPI } from "../../../utils";
import BottomNavigationButtons from "../../BottomNavigationButtons";
import Error from "../../Error";
import AppLoader from "../../AppLoader";
import TestSearchResultRow from "../../../routes/open-test/TestSearchResultRow";

type Props = {
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
    setHistoricData: (data: TestSearchResult[]) => void;
    onConfirm: (ids: number[]) => void;
};

const HistoricDataResultsPopup = ({
    isOpen,
    setIsOpen,
    onConfirm,
    setHistoricData,
}: Props) => {
    const testId = useTestId();
    const [selectedTestsIds, setSelectedTestsIds] = useState<number[]>([]);

    const { value, loading, error } = useAsync(async () => {
        if (!testId) return;
        const historicDataOptions = (await fetcher(HISTORIC_DATA_ENDPOINT, {
            params: {
                test_id: testId,
            },
        })) as TestSearchResultFromAPI[] | null;
        if (!historicDataOptions) return null;

        return convertSearchResultsFromAPI(historicDataOptions);
    }, [testId]);

    useEffect(() => {
        if (value && value?.length > 0) {
            setHistoricData(value);
        }
    }, [value]);

    if (error) return <Error />;

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={() => setIsOpen(false)}
                className="relative z-50"
            >
                <DialogBackdrop className="fixed inset-0 bg-black/70" />
                <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
                    <DialogPanel className="flex flex-col gap-9 w-[950px] 2xl:w-[1400px] bg-white pb-10 pt-9 px-10 rounded-[4px]">
                        <div className="flex w-full items-center justify-center">
                            <DialogTitle className="w-full font-bold text-[22px] text-center text-blue-dark">
                                Select up to 2 previous tests for curve display
                            </DialogTitle>
                            <button
                                className="ml-auto w-6 -mt-6"
                                onClick={() => setIsOpen(false)}
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>
                        {loading ? (
                            <AppLoader />
                        ) : value ? (
                            value.length > 0 ? (
                                <table className="w-[876px] mx-auto mb-10">
                                    <thead className="text-center bg-blue-dark text-white border-b-2 border-black font-bold h-12 text-base">
                                        <tr>
                                            <th></th>
                                            <th>Patient Name</th>
                                            <th>Patient ID</th>
                                            <th>Test Date</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-center">
                                        {value.map((test, index) => {
                                            const isSelected =
                                                selectedTestsIds.includes(
                                                    test.testId
                                                );
                                            const isDisabled =
                                                !isSelected &&
                                                selectedTestsIds.length >= 2;
                                            return (
                                                <TestSearchResultRow
                                                    key={test.testId}
                                                    result={test}
                                                    index={index}
                                                    isSelected={isSelected}
                                                    disabled={isDisabled}
                                                    onSelect={() => {
                                                        setSelectedTestsIds(
                                                            (prev) =>
                                                                isSelected
                                                                    ? prev.filter(
                                                                          (
                                                                              id
                                                                          ) =>
                                                                              id !==
                                                                              test.testId
                                                                      )
                                                                    : [
                                                                          ...prev,
                                                                          test.testId,
                                                                      ]
                                                        );
                                                    }}
                                                    isMultipleSelect
                                                />
                                            );
                                        })}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="w-[876px] mx-auto mb-3">
                                    <p>No results found</p>
                                </div>
                            )
                        ) : null}
                        <BottomNavigationButtons
                            onCancelClick={() => setIsOpen(false)}
                            onConfirmClick={() => {
                                setIsOpen(false);
                                onConfirm(selectedTestsIds);
                            }}
                        />
                    </DialogPanel>
                </div>
            </Dialog>
        </>
    );
};

export default HistoricDataResultsPopup;
